import Button from 'components/Button'
import RadioButtonField from 'features/forms/components/RadioButtonField'
import SubmitButton from 'features/forms/components/SubmitButton'
import { updateRole } from 'features/meetings/graphql/mutation'
import { transformRoleFromValueToEnumValue } from 'features/meetings/utils'
import { showErrorNotification, showNotification } from 'features/notifications/notificationsSlice'
import { Form, Formik } from 'formik'
import { RoleChoices } from 'generated/graphql'
import React from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { PlacementPopover } from 'types'
import { colors, withCapture, _ } from 'utils'
import { ParticipantType } from './Participants'

const S = {
    Wrapper: styled.div<{ placement: PlacementPopover }>`
        position: relative;
        width: 280px;
        background: white;
        border: 1px solid #DADADB;
        box-sizing: border-box;
        border-radius: 3px;
        &::before {
            content: '';
            position: absolute;
            top: ${({ placement }) => (placement === 'bottom' ? '-8px' : 'calc(100% - 8px)')};
            right: 24px;
            width: 16px;
            height: 16px;
            background-color:  ${({ placement }) => (placement === 'bottom' ? '#F8F8F8' : 'white')};;
            border-top: 1px solid #DADADB;
            border-right: 1px solid #DADADB;
            transform: ${({ placement }) => (placement === 'bottom' ? 'rotate(-45deg)' : 'rotate(135deg)')};
        }
    `,
    Header: styled.div`
        background-color: #F8F8F8;
        padding: 10px;
        font-weight: bold;
        font-size: 14px;
        color: ${colors.grayTitle};
        border-top-left-radius: 3px;
        border-top-right-radius: 3px;
    `,
    Body: styled.div`
        padding: 10px 0;
    `,
    Actions: styled.div`
        display: flex;
        align-items: center;
        justify-content: flex-end;
    `
}

interface Props {
    user: ParticipantType,
    placement: PlacementPopover,
    setShowPopover: (show: boolean) => void
    refreshData: () => void
}

const EditRolePopover = ({
    user, placement, setShowPopover, refreshData
}: Props) => {
    const dispatch = useDispatch()
    const handleSubmit = withCapture(async (values) => {
        if (transformRoleFromValueToEnumValue(user.role) === values.role) {
            setShowPopover(false)
            return
        }
        const response = await updateRole(user.userMeetingId, values.role)
        await refreshData()
        if (!response.data || response.errors.length) {
            dispatch(showErrorNotification(response.errors))
            return
        }
        dispatch(showNotification({
            type: 'success',
            body: _('form.notification.update_success'),
        }))
        setShowPopover(false)
    })

    return (
        <Formik
            initialValues={{ role: transformRoleFromValueToEnumValue(user.role) }}
            onSubmit={handleSubmit}
        >
            <S.Wrapper placement={placement}>
                <S.Header>
                    {_('meeting_participant.role.header')}
                </S.Header>
                <S.Body>
                    <Form>
                        <RadioButtonField
                            name="role"
                            options={[
                                {
                                    label: _('meeting_participant.role.audience'),
                                    value: RoleChoices.Audience
                                },
                                {
                                    label: _('meeting_participant.role.speaker'),
                                    value: RoleChoices.Speaker
                                },
                                {
                                    label: _('meeting_participant.role.primary_speaker'),
                                    value: RoleChoices.PrimarySpeaker
                                },
                                {
                                    label: _('meeting_participant.role.moderator'),
                                    value: RoleChoices.Moderator
                                },
                            ]}
                            defaultValue={RoleChoices.Audience}
                            isVerticalAlign
                        />
                        <S.Actions>
                            <Button variant="secondary" onClick={() => setShowPopover(false)}>
                                {_('button.cancel')}
                            </Button>
                            <SubmitButton />
                        </S.Actions>
                    </Form>
                </S.Body>
            </S.Wrapper>
        </Formik>
    )
}

export default EditRolePopover
